import type { MaybeRefOrGetter } from 'vue';
import type { ZodString } from 'zod';

import { toValue } from 'vue';
import { z } from 'zod';

import { ManagedRoleLockedField } from '~/graphql/generated/graphql';

/**
 * Which names of a managed role a school roster keeps up to date, and what that makes of each name
 * field in a form. A locked name is disabled and says who keeps it, in the rendered `hint`, and
 * accepts anything, so an empty roster name never blocks a save. The backend refuses a change to one.
 */
export const useLockedNameFields = (lockedFields: MaybeRefOrGetter<ManagedRoleLockedField[] | null | undefined>, { hint }: { hint?: string } = {}) => {
  const isFieldLocked = (field: ManagedRoleLockedField) => (toValue(lockedFields) ?? []).includes(field);
  const isFirstNameLocked = computed(() => isFieldLocked(ManagedRoleLockedField.FirstName));
  const isLastNameLocked = computed(() => isFieldLocked(ManagedRoleLockedField.LastName));

  const nameFieldOptions = (locked: boolean, editableSchema: ZodString) => ({
    disabled: locked,
    hint: locked ? hint : undefined,
    schema: locked ? z.string() : editableSchema,
  });

  return {
    isFieldLocked,
    isFirstNameLocked,
    isLastNameLocked,
    firstNameFieldOptions: (editableSchema: ZodString) => nameFieldOptions(isFirstNameLocked.value, editableSchema),
    lastNameFieldOptions: (editableSchema: ZodString) => nameFieldOptions(isLastNameLocked.value, editableSchema),
  };
};
